import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Users, FileCheck, Globe, Briefcase, CheckCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
import Footer from '../components/Footer';

export default function LandingPage() {
    const navigate = useNavigate();

    const steps = [
        { icon: <Users className="w-6 h-6" />, title: 'Create Profile', desc: 'Upload your passport data page and join the talent pool' },
        { icon: <FileCheck className="w-6 h-6" />, title: 'Get Verified', desc: 'Complete medical check, police/Interpol clearance and upload reports' },
        { icon: <Briefcase className="w-6 h-6" />, title: 'Receive Offers', desc: 'Gulf employers send you job offers directly' },
    ];

    const features = [
        { icon: <Shield className="w-5 h-5" />, text: 'Verified profiles only' },
        { icon: <Globe className="w-5 h-5" />, text: 'Gulf region employers' },
        { icon: <CheckCircle className="w-5 h-5" />, text: 'Visa processing support' },
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Nav */}
            <nav className="border-b border-zinc-200 px-6 py-4">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <Logo size="lg" data-testid="landing-logo" />
                    <Button
                        variant="outline"
                        className="rounded-none h-12 px-6 border-zinc-900 text-sm font-semibold"
                        onClick={() => navigate('/auth')}
                        data-testid="landing-signin-btn"
                    >
                        Sign In
                    </Button>
                </div>
            </nav>

            {/* Hero */}
            <section className="px-6 pt-16 pb-20 md:pt-24 md:pb-28">
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-[1fr_auto] landscape:max-md:grid-cols-[1fr_auto] gap-8 md:gap-12 items-start">
                    <div>
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-600 mb-6 fade-in" data-testid="hero-overline">
                            Gulf Blue Collar Hiring Platform
                        </p>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] text-zinc-900 mb-8 fade-in stagger-1" data-testid="hero-title">
                            THE GULF'S<br />
                            SPECIALIST PLATFORM<br />
                            FOR BLUE COLLAR HIRING
                        </h1>
                        <p className="text-base sm:text-lg text-zinc-500 max-w-xl mb-12 fade-in stagger-2">
                            Find verified blue collar talent. Fast. A trusted hiring platform connecting
                            skilled workers with Gulf employers. Simple. Direct. Reliable.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 fade-in stagger-3">
                            <Button
                                className="h-14 px-8 rounded-none bg-zinc-900 text-white text-base font-semibold hover:bg-zinc-800 active:scale-[0.98] transition-all"
                                onClick={() => navigate('/auth?tab=register&role=worker')}
                                data-testid="hero-join-worker-btn"
                            >
                                Join as Worker
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                            <Button
                                variant="outline"
                                className="h-14 px-8 rounded-none border-2 border-zinc-900 text-base font-semibold hover:bg-zinc-50 active:scale-[0.98] transition-all"
                                onClick={() => navigate('/auth?tab=register&role=employer')}
                                data-testid="hero-hire-talent-btn"
                            >
                                Hire Talent
                            </Button>
                        </div>

                        {/* Trust signals */}
                        <div className="flex flex-wrap gap-6 mt-12 fade-in stagger-4">
                            {features.map((f, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm text-zinc-500">
                                    {f.icon}
                                    <span>{f.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Logo mark — hidden in mobile portrait (the hero already reads as a complete
                        single-column poster there). Visible on mobile landscape + md+ where there's
                        room to sit beside the heading. Sized by HEIGHT to span the heading vertically:
                        top of "CONNECTING" → bottom of "TO THE GULF". */}
                    <div className="hidden md:flex landscape:max-md:flex justify-end items-start pt-10 fade-in stagger-2" aria-hidden="true">
                        <img
                            src="/gulfbridge-logo-mark.png"
                            alt=""
                            className="h-[144px] lg:h-[180px] w-auto object-contain select-none"
                            draggable="false"
                            loading="eager"
                            decoding="async"
                            data-testid="hero-logo-mark"
                        />
                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="border-t border-zinc-200" />

            {/* How it works */}
            <section className="px-6 py-20">
                <div className="max-w-5xl mx-auto">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-600 mb-4">How It Works</p>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mb-12">
                        Three steps to your new career
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {steps.map((step, i) => (
                            <div key={i} className="border border-zinc-200 p-8 hover:border-zinc-800 transition-colors" data-testid={`step-card-${i}`}>
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="font-heading text-3xl font-black text-zinc-400">0{i + 1}</span>
                                    {step.icon}
                                </div>
                                <h3 className="font-heading text-lg font-bold mb-2">{step.title}</h3>
                                <p className="text-sm text-zinc-500 leading-relaxed">{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="border-t border-zinc-200" />

            {/* For Employers */}
            <section className="px-6 py-20 bg-zinc-50">
                <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-600 mb-4">For Employers</p>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mb-6">
                            Access verified, ready-to-hire talent
                        </h2>
                        <ul className="space-y-4 text-sm text-zinc-600">
                            <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-zinc-900 mt-0.5 flex-shrink-0" />
                                <span>Browse pre-verified worker profiles with documents, skills, and video introductions</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-zinc-900 mt-0.5 flex-shrink-0" />
                                <span>Send job offers directly to candidates with salary, benefits, and location details</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-zinc-900 mt-0.5 flex-shrink-0" />
                                <span>Manage visa processing and travel arrangements all in one platform</span>
                            </li>
                        </ul>
                        <Button
                            className="h-14 px-8 rounded-none bg-zinc-900 text-white mt-8 font-semibold hover:bg-zinc-800"
                            onClick={() => navigate('/auth?tab=register&role=employer')}
                            data-testid="employer-cta-btn"
                        >
                            Start Hiring
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                    </div>
                    <div className="hidden md:block border border-zinc-200 bg-white p-8">
                        <div className="space-y-4">
                            <div className="h-3 bg-zinc-100 rounded-none w-3/4" />
                            <div className="h-3 bg-zinc-100 rounded-none w-1/2" />
                            <div className="border border-zinc-200 p-4 mt-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center">
                                        <User className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <div className="h-2.5 bg-zinc-200 w-24 mb-1.5" />
                                        <div className="h-2 bg-zinc-100 w-16" />
                                    </div>
                                </div>
                                <div className="flex gap-2 mt-3">
                                    <div className="h-6 bg-zinc-100 px-3 text-xs flex items-center">Security</div>
                                    <div className="h-6 bg-zinc-100 px-3 text-xs flex items-center">Driver</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <Footer />
        </div>
    );
}

function User(props) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    );
}
